import React, { useState, useEffect, useCallback } from "react";
import styles from "../styles/resumeBuilder.module.css";
import ResumeForm from "../components/resume-builder/ResumeForm";
import ResumePreview from "../components/resume-builder/ResumePreview";
import ConfigModal from "../components/resume-builder/ConfigModal";
import CustomModal from "../components/CustomModal";
import ConfirmationModal from "../components/ConfirmationModal";
import { ResumeBuilder } from "../lib/ResumeBuilder";
import demoData from "../data/demoResumeData";
import { useResumeBuilder } from "../context/ResumeBuilderContext";
import { useNavigate } from "react-router-dom";

const initialConfig = {
  page: {
    unit: "mm",
    format: "a4",
    orientation: "portrait",
    margins: { top: 15, right: 7, bottom: 15, left: 7 },
  },
  fonts: {
    header: { style: "times", weight: "bold", size: 20, color: "#000000" },
    sectionHeader: {
      style: "times",
      weight: "bold",
      size: 12,
      color: "#000000",
    },
    sectionTitle: {
      style: "times",
      weight: "bold",
      size: 11,
      color: "#000000",
    },
    normal: { style: "times", weight: "normal", size: 11, color: "#000000" },
    small: { style: "times", weight: "bolditalic", size: 9, color: "#000000" },
  },
  spacing: {
    sectionGap: 3,
    headerGap: 6,
    lineGap: 5,
    paragraphGap: 2,
    indentation: 5,
    skillIndentation: 42,
  },
  formatting: { textAlign: { header: "center" }, bulletStyle: "•" },
  divider: { style: "line", color: "#000000", width: 0.2, spacing: 1 },
  sections: {
    header: {
      contactSeparator: " | ",
      icons: {
        size: 3,
        spacing: 1,
        contactSpacing: 4,
        verticalOffset: 0.75,
        urls: {
          email: "https://cdn-icons-png.flaticon.com/128/712/712040.png",
          phone: "https://cdn-icons-png.flaticon.com/128/25/25377.png",
          linkedin: "https://cdn-icons-png.flaticon.com/128/61/61109.png",
          github: "https://cdn-icons-png.flaticon.com/128/733/733609.png",
        },
      },
    },
  },
};

const defaultResumeData = {
  personal: [
    {
      id: "personal-1",
      name: "",
      email: "",
      phone: "",
      github: "",
      linkedin: "",
    },
  ],
  summary: [{ id: "summary-1", summaryText: "" }],
  education: [],
  experience: [],
  projects: [],
  leadership: [],
  skills: [],
};

const ResumeBuilderPage = () => {
  const {
    isPreviewVisible,
    isConfigModalOpen,
    setIsConfigModalOpen,
    resumeName,
    setResumeName,
  } = useResumeBuilder();
  const navigate = useNavigate();

  const [resumeData, setResumeData] = useState(() => {
    try {
      const savedData = localStorage.getItem("builtResumeData");
      return savedData ? JSON.parse(savedData) : defaultResumeData;
    } catch {
      return defaultResumeData;
    }
  });
  const [pdfUrl, setPdfUrl] = useState("");
  const [sectionOrder, setSectionOrder] = useState(() => {
    try {
      const savedOrder = localStorage.getItem("builtResumeOrder");
      return savedOrder
        ? JSON.parse(savedOrder)
        : Object.keys(defaultResumeData);
    } catch {
      return Object.keys(defaultResumeData);
    }
  });
  const [config, setConfig] = useState(() => {
    try {
      const savedConfig = localStorage.getItem("resumeConfig");
      return savedConfig ? JSON.parse(savedConfig) : initialConfig;
    } catch {
      return initialConfig;
    }
  });

  const [showInfoModal, setShowInfoModal] = useState({
    show: false,
    message: "",
  });
  const [showConfirmModal, setShowConfirmModal] = useState({
    show: false,
    type: "",
  });

  useEffect(() => {
    localStorage.setItem("builtResumeData", JSON.stringify(resumeData));
    localStorage.setItem("builtResumeOrder", JSON.stringify(sectionOrder));
    localStorage.setItem("resumeConfig", JSON.stringify(config));
  }, [resumeData, sectionOrder, config]);

  const transformDataForPdf = useCallback((data) => {
    const content = {};
    Object.keys(data).forEach((key) => {
      if (!data[key]) return;
      if (key === "personal") {
        if (data.personal && data.personal.length > 0) {
          const p = data.personal[0];
          content.header = {
            name: p.name,
            contacts: [p.email, p.phone, p.github, p.linkedin].filter(Boolean),
          };
        }
      } else if (key === "summary") {
        if (data.summary && data.summary.length > 0) {
          content.summary = data.summary[0].summaryText;
        }
      } else if (key === "skills") {
        content.skills = (data.skills || []).reduce((acc, skill) => {
          if (skill.category && skill.skills) {
            const skillsArray = Array.isArray(skill.skills)
              ? skill.skills
              : String(skill.skills)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
            if (skillsArray.length > 0) {
              acc[skill.category] = skillsArray;
            }
          }
          return acc;
        }, {});
      } else if (key === "experience") {
        content[key] = (data[key] || []).map((item) => ({
          ...item,
          title: item.company,
          subtitle: item.title,
        }));
      } else if (key === "leadership") {
        content[key] = (data[key] || []).map((item) => ({
          ...item,
          title: item.organization,
          subtitle: item.title,
        }));
      } else {
        content[key] = data[key];
      }
    });
    return content;
  }, []);

  useEffect(() => {
    const builder = new ResumeBuilder(config);
    const content = transformDataForPdf(resumeData);
    const finalOrder = sectionOrder.map((s) =>
      s === "personal" ? "header" : s
    );
    builder.renderContent(content, finalOrder);
    setPdfUrl(builder.getDataUrl());
  }, [resumeData, sectionOrder, config, transformDataForPdf]);

  const addCustomSection = useCallback(
    (name) => {
      if (!name || !name.trim()) return;
      const key = name.trim().toLowerCase().replace(/\s+/g, "_");
      if (resumeData.hasOwnProperty(key)) {
        setShowInfoModal({
          show: true,
          message: "Section with this name already exists.",
        });
        return;
      }
      setResumeData((prev) => ({ ...prev, [key]: [] }));
      setSectionOrder((prev) => [...prev, key]);
    },
    [resumeData]
  );

  const loadDemoData = useCallback(() => {
    const demoDataWithIds = {};
    for (const key in demoData) {
      if (demoData.hasOwnProperty(key)) {
        demoDataWithIds[key] = demoData[key].map((entry) => ({
          ...entry,
          id: `${key}-${Math.random()}`,
        }));
      }
    }
    setResumeData(demoDataWithIds);
    setSectionOrder(Object.keys(demoData));
    setResumeName("Demo Resume");
  }, [setResumeName]);

  useEffect(() => {
    const savedData = localStorage.getItem("builtResumeData");
    if (!savedData) {
      loadDemoData();
    }
  }, [loadDemoData]);

  const handleSaveAndUse = useCallback(() => {
    const builder = new ResumeBuilder(config);
    const content = transformDataForPdf(resumeData);
    const finalOrder = sectionOrder.map((s) =>
      s === "personal" ? "header" : s
    );
    builder.renderContent(content, finalOrder);
    const base64 = builder.doc.output("datauristring");

    let textContent = "";
    Object.entries(resumeData).forEach(([sectionKey, entries]) => {
      textContent += `\n\n--- ${sectionKey.toUpperCase()} ---\n`;
      (entries || []).forEach((entry) => {
        Object.entries(entry).forEach(([key, value]) => {
          if (value && key !== "id") {
            if (key === "bullets" && Array.isArray(value)) {
              value.forEach((bullet) => {
                if (bullet) textContent += `- ${bullet}\n`;
              });
            } else {
              textContent += `${key}: ${value}\n`;
            }
          }
        });
      });
    });

    const assistantResumeData = {
      base64,
      filename: `${resumeName.replace(/\s+/g, "_")}.pdf`,
      text: textContent.trim(),
    };
    localStorage.setItem("resumeData", JSON.stringify(assistantResumeData));
    setShowInfoModal({
      show: true,
      message: "Resume saved and set as active for the Job Assistant!",
    });
  }, [
    config,
    resumeData,
    sectionOrder,
    resumeName,
    navigate,
    transformDataForPdf,
  ]);

  const handleNewResume = useCallback(() => {
    setShowConfirmModal({ show: true, type: "newResume" });
  }, []);

  const confirmNewResume = useCallback(() => {
    setResumeData(defaultResumeData);
    setSectionOrder(Object.keys(defaultResumeData));
    setResumeName("Untitled Resume");
    localStorage.removeItem("builtResumeData");
    localStorage.removeItem("builtResumeOrder");
    setShowConfirmModal({ show: false, type: "" });
  }, [setResumeName]);

  const handleCustomSection = useCallback(() => {
    setShowConfirmModal({ show: true, type: "customSection" });
  }, []);

  const confirmCustomSection = (name) => {
    addCustomSection(name);
    setShowConfirmModal({ show: false, type: "" });
  };

  useEffect(() => {
    const handleDownload = () => {
      const builder = new ResumeBuilder(config);
      const content = transformDataForPdf(resumeData);
      const finalOrder = sectionOrder.map((s) =>
        s === "personal" ? "header" : s
      );
      builder.renderContent(content, finalOrder);
      builder.save(`${resumeName.replace(/\s+/g, "_")}.pdf`);
    };

    window.addEventListener("download-pdf", handleDownload);
    window.addEventListener("load-demo-data", loadDemoData);
    window.addEventListener("add-custom-section", handleCustomSection);
    window.addEventListener("save-and-use", handleSaveAndUse);
    window.addEventListener("create-new-resume", handleNewResume);

    return () => {
      window.removeEventListener("download-pdf", handleDownload);
      window.removeEventListener("load-demo-data", loadDemoData);
      window.removeEventListener("add-custom-section", handleCustomSection);
      window.removeEventListener("save-and-use", handleSaveAndUse);
      window.removeEventListener("create-new-resume", handleNewResume);
    };
  }, [
    loadDemoData,
    addCustomSection,
    handleSaveAndUse,
    handleNewResume,
    transformDataForPdf,
    config,
    resumeData,
    sectionOrder,
    resumeName,
  ]);

  return (
    <>
      {showInfoModal.show && (
        <CustomModal
          message={showInfoModal.message}
          onConfirm={() => {
            setShowInfoModal({ show: false, message: "" });
            if (showInfoModal.message.includes("saved and set as active")) {
              navigate("/dashboard/setup");
            }
          }}
        />
      )}

      {showConfirmModal.show && showConfirmModal.type === "newResume" && (
        <ConfirmationModal
          title="Create New Resume"
          message="Are you sure? This will clear the current resume form and all your changes will be lost."
          onConfirm={confirmNewResume}
          onCancel={() => setShowConfirmModal({ show: false, type: "" })}
        />
      )}

      {showConfirmModal.show && showConfirmModal.type === "customSection" && (
        <ConfirmationModal
          title="Add New Custom Section"
          message="Enter a name for the new section."
          isPrompt={true}
          promptPlaceholder="e.g., Hobbies or Certifications"
          confirmText="Add Section"
          onConfirm={confirmCustomSection}
          onCancel={() => setShowConfirmModal({ show: false, type: "" })}
        />
      )}

      {isConfigModalOpen && (
        <ConfigModal
          config={config}
          setConfig={setConfig}
          closeModal={() => setIsConfigModalOpen(false)}
          initialConfig={initialConfig}
        />
      )}
      <div
        className={`${styles.builderLayout} ${
          !isPreviewVisible ? styles.previewHidden : ""
        }`}
      >
        <div className={styles.formArea}>
          <ResumeForm
            resumeData={resumeData}
            setResumeData={setResumeData}
            sectionOrder={sectionOrder}
            setSectionOrder={setSectionOrder}
            config={config}
          />
        </div>
        {isPreviewVisible && (
          <div className={styles.previewArea}>
            <ResumePreview pdfUrl={pdfUrl} />
          </div>
        )}
      </div>
    </>
  );
};

export default ResumeBuilderPage;
